import { Link } from "react-router-dom";
import SEO from "./SEO";
import PageHeader from "./PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "./Breadcrumbs";

const legalLinks = [
  ["Privacy Policy", "/privacy-policy"],
  ["Terms of Use", "/terms-of-use"],
  ["Cookie Policy", "/cookie-policy"],
  ["Contact", "/contact"],
];

export default function LegalDocument({
  path,
  title,
  seoTitle,
  description,
  eyebrow,
  updatedAt,
  schemaType = "WebPage",
  children,
}) {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: title, to: path },
  ];
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: title,
    description,
    url: `https://www.techniahqservice.com${path}`,
    dateModified: "2026-08-04",
  };

  return (
    <>
      <SEO
        title={seoTitle}
        description={description}
        path={path}
        schema={[buildBreadcrumbSchema(breadcrumbs), pageSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader eyebrow={eyebrow} title={title} description={description} />

      <div className="site-shell py-10 sm:py-14">
        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
          <article className="article-panel legal-document">
            <p className="mb-8 border-b border-neutral-200 pb-5 text-sm font-bold text-neutral-500">
              Last updated {updatedAt}
            </p>
            <div className="grid gap-9 text-base leading-8 text-neutral-700">{children}</div>
          </article>

          <aside className="grid gap-5 lg:sticky lg:top-24">
            <section className="soft-card p-5">
              <h2 className="text-sm font-black uppercase tracking-[0.16em] text-neutral-950">Legal pages</h2>
              <nav className="mt-4 grid gap-2" aria-label="Legal pages">
                {legalLinks.map(([label, to]) => (
                  <Link
                    key={to}
                    to={to}
                    aria-current={to === path ? "page" : undefined}
                    className={`rounded-xl border px-4 py-3 text-sm font-black transition ${
                      to === path
                        ? "border-blue-200 bg-blue-50 text-blue-800"
                        : "border-neutral-200 bg-white text-neutral-700 hover:border-blue-200 hover:text-blue-700"
                    }`}
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            </section>

            <section className="rounded-[1.5rem] border border-blue-100 bg-blue-50 p-5">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">Questions</p>
              <p className="mt-3 text-sm leading-7 text-neutral-700">
                Send privacy, copyright or website questions to the official TechniaHQRobot contact address.
              </p>
              <a
                href="mailto:service@techniahqservice.com"
                className="mt-4 inline-flex text-sm font-black text-blue-700 underline underline-offset-4"
              >
                service@techniahqservice.com
              </a>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}

export function LegalSection({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <h2 className="text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">{title}</h2>
      <div className="mt-4 grid gap-4">{children}</div>
    </section>
  );
}

export function LegalList({ children }) {
  return <ul className="grid list-disc gap-2 pl-6 marker:text-blue-600">{children}</ul>;
}
